<script setup lang="ts">
import type { Ref } from "vue";
import { defu } from "defu";
import SwCategoryNavigation from "../../listing-filters/SwCategoryNavigation.vue";
import { useCmsTranslations } from "@shopware/composables";
import { getTranslatedProperty } from "@shopware/helpers";
import { useCategory, useNavigation } from "#imports";
import { onMounted, ref } from "vue";
import type { Schemas } from "#shopware";

type Translations = {
  listing: {
    category: string;
    subCategory: string;
    subCategoryOf: string;
    loading: string;
    categories: string;
    subCategories: string;
  };
};

let translations: Translations = {
  listing: {
    category: "Category",
    subCategory: "Sub-category",
    subCategoryOf: "of",
    loading: "Loading ...",
    categories: "Categories",
    subCategories: "Sub-categories",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const { category: activeCategory } = useCategory();
const loading: Ref<boolean> = ref(true);
const flagAllowSubcategories: boolean = true; // could be passed maybe as a prop in the future
const categoryNavigation: Ref<Schemas["Category"][]> = ref([]);

const currentCategoryId = activeCategory.value?.id ?? "main-navigation";
const type = flagAllowSubcategories ? currentCategoryId : "main-navigation";
const { loadNavigationElements } = useNavigation({
  type: type,
});
const removeChildrenIfNotActiveCategory = () => {
  const navigation: Schemas["Category"][] = JSON.parse(
    JSON.stringify(categoryNavigation.value)
  );
  return navigation?.map((navigationElement) => {
    navigationElement.children =
      activeCategory.value?.id === navigationElement.id
        ? navigationElement.children
        : [];
    return navigationElement;
  });
};

onMounted(async () => {
  // depth 0 means, we load only first level of categories, depth 1 means we load first and second level of categories ...
  const depth = flagAllowSubcategories ? 0 : 1;
  categoryNavigation.value = await loadNavigationElements({ depth });
  if (!flagAllowSubcategories) {
    categoryNavigation.value = removeChildrenIfNotActiveCategory();
  }
  loading.value = false;
});
</script>
<template>
  <div>
    <div
      v-if="categoryNavigation && categoryNavigation.length"
      class="cms-element-category-navigation max-w-screen-xl -m-[20px]"
    >
      <h4 v-if="categoryNavigation.length > 0 && flagAllowSubcategories">
        {{ $t('listing.subcategories-of') }}
        {{ getTranslatedProperty(activeCategory, "name") }}
      </h4>
      <ListingFiltersSwCategoryNavigation
        :level="0"
        :elements="categoryNavigation"
        :active-category="activeCategory"
      />
      <hr class="border-t border-skin-light opacity-100 my-5" />
    </div>
    <div v-if="loading">
      <div v-for="i in 4" :key="i" class="space-y-3">
        <!-- Accordion header -->
        <div
          class="h-10 bg-gray-200 rounded animate-pulse flex items-center px-4"
        >
          <div class="w-24 h-4 bg-gray-300 rounded"></div>
        </div>
        <!-- Accordion content -->
        <div class="px-0 space-y-2">
          <div
            v-for="j in 3"
            :key="j"
            class="h-4 bg-gray-200 rounded animate-pulse"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>
